import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';

interface DateTimePickerProps {
  value?: string;
  onChange: (date: string, time: string) => void;
  placeholder?: string;
  error?: boolean;
  onBlur?: () => void;
}

const CustomTimeInput = styled.input.attrs({ type: 'time' })`
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  font-size: 16px;
  font-family: inherit;
  background: ${(props: any) => props.theme.colors.interfacePure0};
  transition: all 0.2s ease;
  
  /* remove/define foco do campo */
  outline: none;
  border: 1px solid var(--ce2e8f0);
  border-radius: 8px;
  
  &:focus {
    border-color: #3E4B52;
    box-shadow: 0 0 0 3px rgba(62,75,82,.12);
  }
  
  /* partes do editor (hora/minuto/separador) */
  &::-webkit-datetime-edit {
    color: var(--c353a40);
    padding: 0 2px;
  }
  
  &::-webkit-datetime-edit-hour-field,
  &::-webkit-datetime-edit-minute-field {
    padding: 0 2px; 
    border-radius: 4px;
    border: none;
    outline: none;
  }
  
  &::-webkit-datetime-edit-hour-field:focus,
  &::-webkit-datetime-edit-minute-field:focus {
    background: #E52940; 
    color: ${(props: any) => props.theme.colors.pureWhite}; /* só afeta o campo dentro do input */
  }
  
  /* ícone do picker */
  &::-webkit-calendar-picker-indicator {
    display: none; /* Remove o ícone do seletor visual */
  }
  
  &::-webkit-clear-button,
  &::-webkit-inner-spin-button { 
    display: none; 
  }
`;

export default function DateTimePicker({
  value,
  onChange,
  placeholder = "Selecione a data e hora",
  error= false,
  onBlur
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const parseDateFromValue = (val: string | undefined): Date | null => {
    if (!val) return null;
    
    const dateMatch = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const year = Number(dateMatch[1]);
      const month = Number(dateMatch[2]) - 1;
      const day = Number(dateMatch[3]);
      return new Date(year, month, day);
    }
    
    const dateTimeMatch = val.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (dateTimeMatch) {
      const day = Number(dateTimeMatch[1]);
      const month = Number(dateTimeMatch[2]) - 1;
      const year = Number(dateTimeMatch[3]);
      return new Date(year, month, day);
    }
    
    return null;
  };
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => parseDateFromValue(value));
  
  const extractTimeFromValue = (val: string | undefined): string => {
    if (!val) return '--:--';
    
    // Tenta extrair horário do formato dd/mm/yyyy | hh:mm
    const timeMatch = val.match(/(\d{2}):(\d{2})/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      
      // Valida se o horário é válido
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return `${timeMatch[1]}:${timeMatch[2]}`;
      }
    }
    
    return '--:--';
  };
  
  const [selectedTime, setSelectedTime] = useState<string>(() => extractTimeFromValue(value));
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const [openAbove, setOpenAbove] = useState<boolean>(true);
  const lastValueRef = useRef<string | undefined>(value);

  const focusTimeInput = () => {
    if (timeInputRef.current) {
      timeInputRef.current.focus();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideCalendar = calendarRef.current?.contains(target);
      
      if (!isInsideContainer && !isInsideCalendar) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    // Captura scrolls em quaisquer contêineres roláveis ancestrais
    document.addEventListener('scroll', handleResize, { capture: true, passive: true } as any);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      document.removeEventListener('scroll', handleResize, true as any);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (lastValueRef.current === value) {
      return;
    }
    
    lastValueRef.current = value;
    
    if (value) {
      const parsedDate = parseDateFromValue(value);
      const timeFromValue = extractTimeFromValue(value);
      
      setSelectedDate(parsedDate);
      setSelectedTime(timeFromValue);
    } else {
      setSelectedDate(null);
      setSelectedTime('--:--');
    }
  }, [value]);

  const calculatePosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const calendarWidth = 320;
    // Altura aumentada para garantir que o campo de hora seja sempre visível
    const calendarHeight = 420;

    // Coordenadas em viewport (para position: fixed)
    let left = rect.left;
    const viewportRight = window.innerWidth || document.documentElement.clientWidth;
    const viewportBottom = window.innerHeight || document.documentElement.clientHeight;

    // Ajuste horizontal
    if (left + calendarWidth > viewportRight - 20) {
      left = Math.max(20, viewportRight - calendarWidth - 20);
    }
    if (left < 20) {
      left = 20;
    }

    // Calcula espaço disponível acima e abaixo
    const spaceAbove = rect.top - 20;
    const spaceBelow = viewportBottom - rect.bottom - 20;

    // Prioriza abrir acima se houver espaço suficiente
    let preferAbove = spaceAbove >= calendarHeight;
    
    // Se não couber acima, verifica se cabe abaixo
    if (!preferAbove && spaceBelow < calendarHeight) {
      // Se não couber em nenhum dos dois, escolhe o lado com mais espaço
      preferAbove = spaceAbove > spaceBelow;
    }

    // Calcula top baseado na direção
    let top;
    if (preferAbove) {
      // Abre acima: posiciona o topo do calendário
      top = Math.max(20, rect.top - calendarHeight - 8);
    } else {
      // Abre abaixo: posiciona logo após o input
      top = rect.bottom + 8;
      // Garante que não ultrapasse o limite inferior
      if (top + calendarHeight > viewportBottom - 20) {
        top = Math.max(20, viewportBottom - calendarHeight - 20);
      }
    }

    setOpenAbove(preferAbove);
    setCalendarPosition({ top, left });
  };

  useEffect(() => {
    if (!isOpen) return;
    // Após abrir, mede altura real e ajusta direção se necessário
    const adjustAfterOpen = () => {
      if (!containerRef.current || !calendarRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const height = calendarRef.current.offsetHeight || 420;
      const viewportBottom = window.innerHeight || document.documentElement.clientHeight;
      const viewportRight = window.innerWidth || document.documentElement.clientWidth;

      let left = rect.left;
      const width = calendarRef.current.offsetWidth || 320;
      if (left + width > viewportRight - 20) left = Math.max(20, viewportRight - width - 20);
      if (left < 20) left = 20;

      // Calcula espaço disponível
      const spaceAbove = rect.top - 20;
      const spaceBelow = viewportBottom - rect.bottom - 20;

      // Se está abrindo acima mas não cabe, muda para abaixo se houver mais espaço
      if (openAbove) {
        if (spaceAbove < height && spaceBelow > spaceAbove) {
          setOpenAbove(false);
          const newTop = Math.min(rect.bottom + 8, viewportBottom - height - 20);
          setCalendarPosition({ top: newTop, left });
          return;
        }
        setCalendarPosition({ top: Math.max(20, rect.top - height - 8), left });
      } else {
        // Se está abaixo mas não cabe, tenta acima se houver mais espaço
        if (spaceBelow < height && spaceAbove > spaceBelow) {
          setOpenAbove(true);
          setCalendarPosition({ top: Math.max(20, rect.top - height - 8), left });
          return;
        }
        const newTop = Math.min(rect.bottom + 8, viewportBottom - height - 20);
        setCalendarPosition({ top: Math.max(20, newTop), left });
      }
    };

    // Usa rAF para garantir que o DOM do calendário foi pintado
    const id = requestAnimationFrame(adjustAfterOpen);
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  const formatDateForDisplay = (date: Date, time: string) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year} ${time}`;
  };

  const formatDateForValue = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Dias do mês anterior
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonth.getDate() - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonth.getDate() - i)
      });
    }
    
    // Dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day)
      });
    }
    
    // Dias do próximo mês para completar a grade
    const remainingDays = 42 - days.length; // 6 semanas * 7 dias
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day)
      });
    }
    
    return days;
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    // Quando seleciona data pelo calendário, mantém o calendário aberto
    // e foca no campo de hora para o usuário definir o horário
    setSelectedTime('--:--');
    
    // Foca no campo de hora após um pequeno delay
    setTimeout(() => {
      focusTimeInput();
    }, 100);
  };

  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
    if (selectedDate && time !== '--:--') {
      onChange(formatDateForValue(selectedDate), time);
    }
  };

  const handleTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter fecha o calendário
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      return;
    }
    
    // Escape também fecha
    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const days = getDaysInMonth(currentDate);

  // ===== Máscara inteligente =====
  const [inputValue, setInputValue] = useState<string>('');
  const [rawValue, setRawValue] = useState<string>('');

  // Atualiza o campo quando selectedDate ou selectedTime mudam
  useEffect(() => {
    if (selectedDate) {
      const day = selectedDate.getDate().toString().padStart(2, '0');
      const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
      const year = selectedDate.getFullYear();
      
      if (!selectedTime || selectedTime === '--:--') {
        setInputValue(`${day}/${month}/${year}`);
        setRawValue(`${day}${month}${year}`);
      } else {
        setInputValue(`${day}/${month}/${year} ${selectedTime}`);
        setRawValue(`${day}${month}${year}${selectedTime.replace(':', '')}`);
      }
    } else {
      setInputValue('');
      setRawValue('');
    }
  }, [selectedDate, selectedTime]);

  // Aplica máscara conforme o usuário digita
  const applyMask = (value: string): string => {
    // Remove tudo que não é dígito
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 0) return '';
    
    let masked = '';
    
    // DD
    if (digits.length >= 1) {
      masked += digits.substring(0, 2);
    }
    
    // /MM
    if (digits.length >= 3) {
      masked += '/' + digits.substring(2, 4);
    }
    
    // /YYYY
    if (digits.length >= 5) {
      masked += '/' + digits.substring(4, 8);
    }
    
    // HH
    if (digits.length >= 9) {
      masked += ' ' + digits.substring(8, 10);
    }
    
    // :MM
    if (digits.length >= 11) {
      masked += ':' + digits.substring(10, 12);
    }
    
    return masked;
  };

  // Função para parsear a entrada do usuário
  const parseUserInput = (str: string) => {
    if (!str || str.trim() === '') {
      setSelectedDate(null);
      setSelectedTime('--:--');
      return;
    }
    
    // Extrai apenas os dígitos
    const digits = str.replace(/\D/g, '');
    
    // Precisa ter pelo menos 8 dígitos para data completa (ddmmyyyy)
    if (digits.length < 8) {
      return; // Não faz nada, mantém o estado atual
    }
    
    const d = parseInt(digits.substring(0, 2));
    const m = parseInt(digits.substring(2, 4));
    const y = parseInt(digits.substring(4, 8));
    
    // Valida a data
    if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1900) {
      return;
    }
    
    const newDate = new Date(y, m - 1, d);
    
    // Se tem horário (12 dígitos)
    if (digits.length === 12) {
      const hh = parseInt(digits.substring(8, 10));
      const mm = parseInt(digits.substring(10, 12));
      
      if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
        const timeString = `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
        setSelectedDate(newDate);
        setSelectedTime(timeString);
        onChange(formatDateForValue(newDate), timeString);
        return;
      }
    }
    
    // Apenas data
    setSelectedDate(newDate);
    setSelectedTime('--:--');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const digits = newValue.replace(/\D/g, '');
    
    // Limita a 12 dígitos (ddmmyyyyhhmm)
    if (digits.length <= 12) {
      setRawValue(digits);
      setInputValue(applyMask(newValue));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    
    // Permite Ctrl+A, Ctrl+C, Ctrl+V, etc
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    
    // Permite navegação
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(e.key)) {
      return;
    }
    
    // Backspace ou Delete - permite apagar
    if (e.key === 'Backspace' || e.key === 'Delete') {
      return;
    }
    
    // Enter - sincroniza os dados
    if (e.key === 'Enter') {
      e.preventDefault();
      target.blur();
      return;
    }
    
    // Escape - fecha o modal se estiver aberto
    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    
    // Permite apenas números
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };


  return (
    <div ref={containerRef} style={{ position: 'relative', zIndex: isOpen ? 1000 : 1 }}>
      {/* Input Field com máscara */}
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onKeyDown={handleKeyDown}
        onChange={handleInputChange}
        onClick={() => {
          if (!isOpen) {
            calculatePosition();
          }
          setIsOpen(true);
        }}
        onFocus={(e) => { 
          e.target.style.border = '1px solid #3E4B52';
        }}
        onBlur={(e) => { 
          e.target.style.border = '1px solid var(--ce2e8f0)';
          if (e.target.value) {
            parseUserInput(e.target.value);
          } else {
            setSelectedDate(null);
            setSelectedTime('--:--');
          }
          if (onBlur) onBlur();
        }}
        style={{
          width: '100%',
          height: '48px',
          background: 'var(--cffffff)',
            border: !error ? '1px solid var(--ce2e8f0)' : '1px solid #EA0000',
          outline: 'none',
          borderRadius: '8px',
          color: 'var(--c353a40)',
          padding: '12px 16px',
          fontSize: '16px',
          fontWeight: '400',
          lineHeight: '24px',
          transition: 'all 0.2s ease',
          fontFamily: 'inherit'
        }}
      />

      {/* Calendar Dropdown */}
      {isOpen && createPortal(
        <div ref={calendarRef} style={{
          position: 'fixed',
          top: calendarPosition.top,
          left: calendarPosition.left,
          backgroundColor: 'var(--cffffff)',
          border: '1px solid var(--ce2e8f0)',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 10000,
          padding: '16px',
          minWidth: '320px'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <button
              onClick={handlePrevMonth}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid var(--ce2e8f0)',
                borderRadius: '4px',
                background: 'var(--cffffff)',
                color: 'var(--c353a40)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              &lt;
            </button>
            
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--c353a40)'
            }}>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>
            
            <button
              onClick={handleNextMonth}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid var(--ce2e8f0)',
                borderRadius: '4px',
                background: 'var(--cffffff)',
                color: 'var(--c353a40)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              &gt;
            </button>
          </div>

          {/* Days of Week */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginBottom: '8px'
          }}>
            {dayNames.map((day) => (
              <div
                key={day}
                style={{
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--c353a40)',
                  padding: '8px 4px'
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginBottom: '16px'
          }}>
            {days.map((day, index) => (
              <button
                key={index}
                onClick={() => handleDateSelect(day.date)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: isSelected(day.date) ? '1px solid #E52940' : 'none',
                  borderRadius: '4px',
                  background: isToday(day.date) ? 'var(--cf2f2f2)' : 'var(--cffffff)',
                  color: day.isCurrentMonth ? 'var(--c353a40)' : 'var(--c798891)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: isSelected(day.date) ? 'bold' : (isToday(day.date) ? '600' : '400'),
                  transition: 'all 0.2s ease',
                  margin: '0 auto'
                }}
                onMouseEnter={(e) => {
                  if (day.isCurrentMonth) {
                    e.currentTarget.style.backgroundColor = 'var(--cf2f2f2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (day.isCurrentMonth) {
                    e.currentTarget.style.backgroundColor = isToday(day.date) ? 'var(--cf2f2f2)' : 'var(--cffffff)';
                  }
                }}
              >
                {day.day}
              </button>
            ))}
          </div>

          {/* Time Selector */}
          <div style={{
            borderTop: '1px solid var(--ce2e8f0)',
            paddingTop: '16px'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--c353a40)',
              marginBottom: '8px'
            }}>
              Horário:
            </div>
            <CustomTimeInput
              ref={timeInputRef}
              value={selectedTime === '--:--' ? '' : selectedTime}
              onChange={(e) => handleTimeChange(e.target.value || '--:--')}
              onKeyDown={handleTimeKeyDown}
              onBlur={onBlur}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
